import React, { useEffect, useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { applyAdminTheme } from '../lib/theme';
import Logo from '../components/Logo';
import './admin.css';

/**
 * Panelin kendi giriş ekranı.
 *
 * Panel ayrı bir alan adına taşındığında portalın giriş penceresi orada
 * yoktur; yönetici bu ekrandan girer. Oturum origin başına ayrı tutulduğu
 * için bu, ana sitedeki oturumdan bağımsız bir girişidir — istenen budur:
 * portalda açılan bir açık panelin oturumunu göremez.
 *
 * İki adımlı doğrulama aynı akışı kullanır (AuthModal'daki mantığın paneldeki
 * karşılığı): kod, hesabın seçili kanalından gider ve challenge kaydı hangi
 * kanalın kullanıldığını bilir.
 *
 * Şifre sıfırlama bilinçli olarak buradan yapılmaz: sıfırlama bağlantısı
 * yaşayan ana siteye gider (posta şablonundaki adres oradan üretilir).
 */
const AdminLogin = () => {
  const settings = useSettings();
  const { login, verifyTwoFactor, resendTwoFactor } = useAuth();
  const [form, setForm] = useState({ username: '', password: '' });
  const [twoFactor, setTwoFactor] = useState(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // Panel adresinde portal teması hiç yüklenmez; kabuğu panelin kendi
  // değişkenleri boyar (AdminLayout ile aynı çağrı).
  useEffect(() => {
    applyAdminTheme(settings.primary_color, settings.secondary_color);
  }, [settings.primary_color, settings.secondary_color]);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    const result = await login(form.username.trim(), form.password);
    setBusy(false);
    if (result.ok) return;
    if (result.twoFactor) {
      setTwoFactor(result.twoFactor);
      setCode('');
      return;
    }
    setError(result.msg || 'Giriş başarısız');
  };

  const submitCode = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    const result = await verifyTwoFactor(
      twoFactor.challenge, code.trim(), twoFactor.purpose,
    );
    setBusy(false);
    if (!result.ok) setError(result.msg || 'Doğrulama başarısız');
  };

  const resend = async () => {
    setBusy(true);
    setError('');
    const result = await resendTwoFactor(twoFactor.challenge, twoFactor.purpose);
    setBusy(false);
    if (!result.ok) setError(result.msg || 'Kod gönderilemedi');
  };

  return (
    <div className="admin-login-shell">
      <div className="admin-card admin-login-card">
        <div className="admin-login-brand">
          <Logo linked={false} />
          <div className="admin-login-title">
            <ShieldCheck size={16} />
            <span>Yönetim Paneli</span>
          </div>
        </div>

        {error && <div className="admin-login-error">{error}</div>}

        {twoFactor ? (
          <form onSubmit={submitCode} className="admin-login-form">
            <p className="admin-login-hint">
              {twoFactor.deliveryChannel === 'totp'
                ? 'Google Authenticator uygulamanızdaki güncel kodu girin.'
                : `Doğrulama kodu${twoFactor.deliveryTarget ? ` ${twoFactor.deliveryTarget}` : ''} adresine gönderildi.`}
            </p>
            <input
              className="admin-input"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={twoFactor.codeLength || 6}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Doğrulama kodu"
              autoFocus
            />
            <button className="admin-btn admin-btn-primary" disabled={busy || !code.trim()}>
              {busy ? 'Doğrulanıyor…' : 'Doğrula'}
            </button>
            {twoFactor.deliveryChannel !== 'totp' && (
              <button
                type="button"
                className="admin-btn admin-btn-secondary"
                onClick={resend}
                disabled={busy}
              >
                Kodu tekrar gönder
              </button>
            )}
          </form>
        ) : (
          <form onSubmit={submit} className="admin-login-form">
            <input
              className="admin-input"
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              placeholder="Kullanıcı adı"
              autoComplete="username"
              autoFocus
            />
            <input
              className="admin-input"
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="Şifre"
              autoComplete="current-password"
            />
            <button
              className="admin-btn admin-btn-primary"
              disabled={busy || !form.username.trim() || !form.password}
            >
              {busy ? 'Giriş yapılıyor…' : 'Giriş yap'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default AdminLogin;
